/**
 * Bearer-fetch client for `integrationsApi` (Task 7,
 * `functions/src/api/integrations/index.ts`) — mirrors the pattern in
 * `packages/core/src/users/organization-client.ts`. Owner-only endpoints:
 * the server re-verifies `globalRole=tenant_owner` on every call, this
 * client does no authorization of its own.
 *
 * IMPORTANT: credentials (the connect request body's `credentials` object)
 * must NEVER be logged. This file must not call `console.log`/`console.error`
 * with the request or response body — on failure it only surfaces the HTTP
 * status, never the payload.
 */

async function bearer(
  url: string,
  idToken: string,
  init: {
    method?: string;
    headers?: Record<string, string>;
    body?: unknown;
  } = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    ...(init.headers ?? {}),
  };
  const reqInit: RequestInit = {};
  if (init.method !== undefined) reqInit.method = init.method;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    reqInit.body = JSON.stringify(init.body);
  }
  reqInit.headers = headers;
  return fetch(url, reqInit);
}

export type IntegrationDirection = "inbound" | "outbound" | "bidirectional";

/** How the web UI should render one owner-editable config field. Mirrors
 *  `ConfigFieldDescriptor` (`packages/integrations/src/contract.ts`) — the
 *  connector declares WHAT to render, this DTO carries it across the wire. */
export type ConfigFieldKind = "text" | "number" | "hotel" | "multiselect" | "employeeMap";

export interface ConfigFieldDTO {
  readonly name: string;
  readonly kind: ConfigFieldKind;
  readonly required?: boolean;
  /** `multiselect` only — the allowed values. */
  readonly options?: ReadonlyArray<string>;
  /** `number` only. */
  readonly min?: number;
  readonly max?: number;
}

/** One row of `GET /api/integrations` — catalogue entry joined with the
 *  tenant's per-integration config. Never carries credentials. */
export interface IntegrationSummaryDTO {
  readonly id: string;
  readonly displayName: string;
  readonly direction: IntegrationDirection;
  readonly status: "connected" | "disconnected" | string;
  readonly lastRunAt: unknown | null;
  readonly lastStatus: string | null;
  /** Perché l'ultima sincronizzazione è fallita. `null` quando è andata bene. */
  readonly lastError: string | null;
  /** Outcome of the last sync, per record kind. Surfaced so the owner can see WHAT
   *  came in: a run that says "ok" while importing zero absences is a real failure
   *  mode, and the counts are the only thing that makes it visible. */
  readonly lastRun?: {
    readonly pulled: number;
    readonly upserted: number;
    readonly failed: number;
    readonly byKind?: Record<string, { created: number; updated: number; skipped: number }>;
    /** External employee identifiers whose records found nobody in Praesto to
     *  attach to — the count alone is unactionable, it says something is
     *  missing without saying who.
     *
     *  These are NOT guaranteed to be opaque ids: DiC derives them from the
     *  employee's name when its export carries no numeric id, so this list can
     *  contain personal names. It is safe here only because the integrations
     *  panel is owner-gated and an owner already sees the full staff roster.
     *  Rendering it anywhere less restricted needs a rethink. */
    readonly unmatched?: ReadonlyArray<string>;
  } | null;
  /** Current sync cadence, or `null` if never configured (PUT /:id/config
   *  not yet called for this integration). */
  /**
   * `operativa` e `mattina_e_sera` sono ORE DEL GIORNO (6/16/21 e 6/21), non
   * intervalli: vedi `isDue` lato server. Un intervallo deriva finché la
   * sincronizzazione «giornaliera» non cade di notte.
   */
  readonly cadence?: "hourly" | "operativa" | "mattina_e_sera" | "daily" | null;
  /** The tenant's stored connector-specific config (e.g. TeamSystem's
   *  `employeeMap`) — NOT secret, credentials live elsewhere. Needed to
   *  (a) pre-fill config editors after a reload and (b) preserve this object
   *  when the UI only intends to change `cadence`, since the server's
   *  `PUT /:id/config` overwrites the whole `config` field on every call. */
  readonly config?: Record<string, unknown>;
  /** Ingestion mode: "api" (default) for API-based connectors, "file" for
   *  file-upload connectors like TeamSystem. Lets the UI decide whether to
   *  show credential forms or file-upload controls. */
  readonly ingestion?: "api" | "file";
  /** Top-level field names of the connector's `credentialSchema` (Task 7,
   *  `credentialFieldsOf` in `functions/src/api/integrations/index.ts`) —
   *  lets the UI render a credential form without a hand-maintained map. */
  readonly credentialFields: ReadonlyArray<string>;
  /** Owner-editable config fields the connector declares (Task 3,
   *  `descriptor.configFields`) — lets the UI render a connect/config form
   *  without a hand-maintained per-connector-id branch. Never carries
   *  connector-owned state (e.g. `lastLandedExportId`, the api connector's
   *  `employeeMap`) — that lives server-side under `state` and is never sent
   *  to the client. */
  readonly configFields: ReadonlyArray<ConfigFieldDTO>;
}

export interface ConnectIntegrationInput {
  readonly credentials: Record<string, unknown>;
  readonly config: Record<string, unknown>;
}

export interface UpdateIntegrationConfigInput {
  /** Omit to preserve the currently stored cadence — the server no longer
   *  requires it on every config save (Task 3), so a config-only save (e.g.
   *  fixing a field an alert flagged) doesn't need to resend it. */
  readonly cadence?: "hourly" | "operativa" | "mattina_e_sera" | "daily";
  readonly config: Record<string, unknown>;
}

export interface TestIntegrationResult {
  readonly ok: boolean;
  readonly message?: string;
}

export interface SyncIntegrationResult {
  readonly status: string;
  readonly runId: string | null;
}

/**
 * Typed error for `uploadIntegrationExport` failures. Carries specific error
 * codes from the server (`file_too_large`, `mime_not_allowed`, `missing_file`)
 * so the modal can surface actionable messages to the user. Pattern mirrors
 * `OccupancyImportError` in `shift-client.ts`.
 */
export class UploadExportError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly maxBytes: number | undefined;
  readonly mime: string | undefined;

  constructor(status: number, body: unknown) {
    let code: string | undefined;
    let maxBytes: number | undefined;
    let mime: string | undefined;

    if (typeof body === "object" && body !== null) {
      const b = body as Record<string, unknown>;
      if (typeof b.error === "string") code = b.error;
      if (typeof b.maxBytes === "number") maxBytes = b.maxBytes;
      if (typeof b.mime === "string") mime = b.mime;
    }

    super(`uploadIntegrationExport failed: ${status}${code ? ` (${code})` : ""}`);
    this.name = "UploadExportError";
    this.status = status;
    this.code = code;
    this.maxBytes = maxBytes;
    this.mime = mime;
  }
}

export async function listIntegrations(
  apiBase: string,
  idToken: string,
): Promise<{ integrations: ReadonlyArray<IntegrationSummaryDTO> }> {
  const res = await bearer(`${apiBase}/api/integrations`, idToken);
  if (!res.ok) throw new Error(`listIntegrations failed: ${res.status}`);
  return (await res.json()) as { integrations: ReadonlyArray<IntegrationSummaryDTO> };
}

/** `POST /:id/connect` — body is EXACTLY `{ credentials, config }` (two
 *  separate objects, matching the server's per-field zod validation). */
export async function connectIntegration(
  apiBase: string,
  id: string,
  input: ConnectIntegrationInput,
  idToken: string,
): Promise<{ ok: true }> {
  const res = await bearer(`${apiBase}/api/integrations/${id}/connect`, idToken, {
    method: "POST",
    body: input,
  });
  if (!res.ok) throw new Error(`connectIntegration failed: ${res.status}`);
  return (await res.json()) as { ok: true };
}

/** `POST /:id/test` — re-runs `testConnection` against the stored (decrypted
 *  server-side) credentials. No body. */
export async function testIntegration(
  apiBase: string,
  id: string,
  idToken: string,
): Promise<TestIntegrationResult> {
  const res = await bearer(`${apiBase}/api/integrations/${id}/test`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`testIntegration failed: ${res.status}`);
  return (await res.json()) as TestIntegrationResult;
}

/** `PUT /:id/config` — cadence + connector-specific config (never credentials). */
export async function updateIntegrationConfig(
  apiBase: string,
  id: string,
  input: UpdateIntegrationConfigInput,
  idToken: string,
): Promise<{ ok: true }> {
  const res = await bearer(`${apiBase}/api/integrations/${id}/config`, idToken, {
    method: "PUT",
    body: input,
  });
  if (!res.ok) throw new Error(`updateIntegrationConfig failed: ${res.status}`);
  return (await res.json()) as { ok: true };
}

/** `POST /:id/sync` — manual "Sync now", shares the engine with the scheduler. */
export async function syncIntegration(
  apiBase: string,
  id: string,
  idToken: string,
): Promise<SyncIntegrationResult> {
  const res = await bearer(`${apiBase}/api/integrations/${id}/sync`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`syncIntegration failed: ${res.status}`);
  return (await res.json()) as SyncIntegrationResult;
}

/** `POST /:id/disconnect` — wipes stored credentials, flips status. */
export async function disconnectIntegration(
  apiBase: string,
  id: string,
  idToken: string,
): Promise<{ ok: true }> {
  const res = await bearer(`${apiBase}/api/integrations/${id}/disconnect`, idToken, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`disconnectIntegration failed: ${res.status}`);
  return (await res.json()) as { ok: true };
}

/** `POST /:id/upload-export` — owner uploads a Dipendenti in Cloud export
 *  (`.xlsx` or Generale `.zip`) for a file-mode connector (Task 7,
 *  `functions/src/api/integrations/index.ts`). Multipart, so it hits `fetch`
 *  directly with a `FormData` body (NOT `bearer()`, which forces a JSON
 *  content-type) — mirrors `shift-client.importOccupancy`. Returns the Cloud
 *  Storage path the server wrote it to; the connector's next scheduled
 *  `pull` (file-source.ts) reads the file from there. Throws `UploadExportError`
 *  with specific error codes (`file_too_large`, `mime_not_allowed`, `missing_file`)
 *  so the modal can surface actionable messages. */
export async function uploadIntegrationExport(
  apiBase: string,
  id: string,
  file: File,
  idToken: string,
): Promise<{ storagePath: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  const res = await fetch(`${apiBase}/api/integrations/${id}/upload-export`, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
    body: form,
  });
  if (!res.ok) {
    let body: unknown = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON body (e.g. size cap / proxy error) — leave empty */
    }
    throw new UploadExportError(res.status, body);
  }
  return (await res.json()) as { storagePath: string };
}
